import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
    Search,
    ArrowLeft,
    Rocket,
    Brain,
    Layout,
    ChevronRight,
    SearchX,
    MessageSquare,
    BookOpen
} from 'lucide-react';
import { HELP_DATA } from '../../data/helpData';
import { LandingNavbar } from '../landing/LandingNavbar';
import { LandingFooter } from '../landing/LandingFooter';

interface HelpCenterProps {
    onBack: () => void;
    onSelectArticle: (articleId: string) => void;
    onGetStarted: () => void;
    onPrivacyPolicy?: () => void;
}

export const HelpCenter: React.FC<HelpCenterProps> = ({ onBack, onSelectArticle, onGetStarted, onPrivacyPolicy }) => {
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(null);
    const searchRef = React.useRef<HTMLInputElement>(null);

    // Fix scrolling and scroll to top
    useEffect(() => {
        document.body.style.overflow = 'auto';
        window.scrollTo(0, 0);
        return () => {
            document.body.style.overflow = '';
        };
    }, []);

    // Command+K to search
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
                e.preventDefault();
                searchRef.current?.focus();
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, []);

    const filteredArticles = (searchQuery || selectedCategoryId)
        ? HELP_DATA.articles.filter(article => {
            const matchesSearch = !searchQuery ||
                article.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                article.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
                article.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()));

            const matchesCategory = !selectedCategoryId || article.category === selectedCategoryId;

            return matchesSearch && matchesCategory;
        })
        : HELP_DATA.articles.slice(0, 6);

    const getIcon = (iconName: string) => {
        switch (iconName) {
            case 'Rocket': return <Rocket className="w-8 h-8" />;
            case 'Brain': return <Brain className="w-8 h-8" />;
            case 'Layout': return <Layout className="w-8 h-8" />;
            default: return <BookOpen className="w-8 h-8" />;
        }
    };

    return (
        <div className="min-h-screen bg-slate-950 text-slate-200">
            <LandingNavbar onGetStarted={onGetStarted} />

            {/* Back Navigation */}
            <div className="pt-28 pb-10 px-6 max-w-7xl mx-auto border-b border-slate-900">
                <button
                    onClick={onBack}
                    className="flex items-center gap-2 text-sm font-bold text-slate-500 hover:text-white transition-colors uppercase tracking-widest"
                >
                    <ArrowLeft className="w-4 h-4" />
                    Back to NoteSpace
                </button>
            </div>

            <main className="max-w-7xl mx-auto px-6 py-20">
                {/* Hero Section */}
                <section className="mb-24">
                    <motion.h1
                        className="text-6xl md:text-8xl font-black text-white mb-10 tracking-tighter"
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                    >
                        Docs & <br /><span className="text-indigo-500">Resources.</span>
                    </motion.h1>

                    <div className="max-w-3xl relative">
                        <div className="absolute inset-y-0 left-6 flex items-center pointer-events-none">
                            <Search className="w-6 h-6 text-slate-500" />
                        </div>
                        <input
                            type="text"
                            ref={searchRef}
                            placeholder="Find features, tips, or troubleshooting guides..."
                            className="w-full bg-slate-900 border-2 border-slate-800 rounded-3xl py-6 pl-16 pr-6 text-xl text-white placeholder-slate-600 focus:outline-none focus:border-indigo-500 transition-all shadow-2xl"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                        />
                        <div className="absolute top-1/2 -translate-y-1/2 right-6 hidden md:flex items-center gap-2 px-3 py-1.5 rounded-lg bg-slate-950 border border-slate-800 text-xs font-bold text-slate-500 uppercase">
                            <span>Cmd</span>
                            <span>K</span>
                        </div>
                    </div>
                </section>

                <AnimatePresence mode="wait">
                    {searchQuery || selectedCategoryId ? (
                        <motion.section
                            key="search-results"
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -20 }}
                            className="space-y-12"
                        >
                            <div className="flex items-end justify-between border-b-2 border-slate-900 pb-6">
                                <div>
                                    <h2 className="text-4xl font-black text-white tracking-tight uppercase">
                                        {searchQuery ? 'Results' : selectedCategoryId?.replace('-', ' ')}
                                    </h2>
                                    <p className="text-slate-500 mt-2 font-medium">
                                        Showing {filteredArticles.length} relevant articles
                                    </p>
                                </div>
                                <button
                                    onClick={() => { setSearchQuery(''); setSelectedCategoryId(null); }}
                                    className="px-6 py-3 rounded-xl bg-slate-900 text-slate-300 font-bold hover:bg-slate-800 transition-colors uppercase tracking-widest text-xs"
                                >
                                    Clear Filters
                                </button>
                            </div>

                            {filteredArticles.length > 0 ? (
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    {filteredArticles.map(article => (
                                        <button
                                            key={article.id}
                                            onClick={() => onSelectArticle(article.id)}
                                            className="w-full text-left p-8 bg-slate-900 border-2 border-transparent hover:border-indigo-500 rounded-3xl transition-all group"
                                        >
                                            <div className="flex justify-between items-start gap-4">
                                                <div className="space-y-4">
                                                    <span className="inline-block px-3 py-1 rounded-full bg-slate-950 text-indigo-400 text-[10px] font-black uppercase tracking-tighter">
                                                        {article.category.replace('-', ' ')}
                                                    </span>
                                                    <h3 className="text-3xl font-black text-white group-hover:text-indigo-400 transition-colors leading-tight">
                                                        {article.title}
                                                    </h3>
                                                    <p className="text-slate-400 font-medium leading-relaxed">
                                                        {article.description}
                                                    </p>
                                                </div>
                                                <div className="p-3 rounded-2xl bg-slate-950 text-slate-600 group-hover:text-indigo-500 transition-colors">
                                                    <ArrowLeft className="w-6 h-6 rotate-180" />
                                                </div>
                                            </div>
                                        </button>
                                    ))}
                                </div>
                            ) : (
                                <div className="text-center py-32 bg-slate-900 rounded-5xl border-2 border-dashed border-slate-800">
                                    <SearchX className="w-20 h-20 text-slate-700 mx-auto mb-6" />
                                    <h3 className="text-3xl font-black text-white mb-2">No matches found.</h3>
                                    <p className="text-slate-500 text-lg mb-8">Try adjusting your keywords or browse categories below.</p>
                                    <button
                                        onClick={() => { setSearchQuery(''); setSelectedCategoryId(null); }}
                                        className="px-10 py-5 bg-indigo-600 text-white rounded-2xl font-black hover:bg-indigo-500 transition-colors shadow-2xl shadow-indigo-600/20 uppercase tracking-widest"
                                    >
                                        Browse all Docs
                                    </button>
                                </div>
                            )}
                        </motion.section>
                    ) : (
                        <motion.section
                            key="categories"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                        >
                            <h2 className="text-sm font-black uppercase tracking-[0.3em] text-slate-600 mb-12">System Categories</h2>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                                {HELP_DATA.categories.map((category) => (
                                    <button
                                        key={category.id}
                                        onClick={() => setSelectedCategoryId(category.id)}
                                        className="text-left p-12 bg-slate-900 border-2 border-transparent hover:border-slate-700 rounded-4xl transition-all group flex flex-col h-full"
                                    >
                                        <div className="w-20 h-20 rounded-3xl bg-slate-950 flex items-center justify-center text-indigo-500 mb- aggregation-8 group-hover:scale-110 group-hover:-rotate-3 transition-transform">
                                            {getIcon(category.icon)}
                                        </div>
                                        <h3 className="text-4xl font-black text-white mb-4 tracking-tighter transition-colors group-hover:text-indigo-400">
                                            {category.title}
                                        </h3>
                                        <p className="text-slate-500 text-lg font-medium leading-relaxed mb-10 flex-1">
                                            {category.description}
                                        </p>
                                        <div className="inline-flex items-center gap-2 text-indigo-500 font-black text-sm uppercase tracking-widest">
                                            Open Collection
                                            <ChevronRight className="w-5 h-5 transition-transform group-hover:translate-x-2" />
                                        </div>
                                    </button>
                                ))}
                            </div>

                            <div className="mt-32 p-20 bg-indigo-600 rounded-5xl text-white flex flex-col md:flex-row items-center justify-between gap-12 relative overflow-hidden group">
                                <div className="absolute top-0 right-0 w-96 h-96 bg-white/10 rounded-full blur-3xl -mr-20 -mt-20 group-hover:scale-150 transition-transform duration-1000" />
                                <div className="relative z-10 text-center md:text-left space-y-4">
                                    <h2 className="text-5xl font-black tracking-tighter">Need human help?</h2>
                                    <p className="text-indigo-100 text-xl font-medium max-w-lg">Our engineering team is standing by to help you solve your most complex workflow problems.</p>
                                </div>
                                <button className="relative z-10 bg-white text-indigo-600 px-12 py-6 rounded-3xl font-black text-xl hover:bg-slate-100 transition-all shadow-2xl flex items-center gap-4 group">
                                    <MessageSquare className="w-7 h-7" />
                                    Contact Support
                                </button>
                            </div>
                        </motion.section>
                    )}
                </AnimatePresence>
            </main>

            <LandingFooter onPrivacyPolicy={onPrivacyPolicy} />
        </div>
    );
};
